import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Bell, Filter, CheckCheck, Trash2, Loader2,
  UserPlus, MapPin, Award, Send, Trophy, XCircle, Car, MessageSquare,
  Briefcase, Play, CheckCircle, Crosshair, FileCheck, Receipt, DollarSign, Percent,
} from "lucide-react";
import { toast } from "sonner";

const ICON_MAP = {
  "user-plus": UserPlus, "map-pin": MapPin, "award": Award, "send": Send,
  "trophy": Trophy, "x-circle": XCircle, "car": Car, "message-square": MessageSquare,
  "briefcase": Briefcase, "play": Play, "check-circle": CheckCircle, "crosshair": Crosshair,
  "file-check": FileCheck, "receipt": Receipt, "dollar-sign": DollarSign, "percent": Percent,
};
const COLOR_MAP = {
  blue: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  cyan: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  violet: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rose: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  slate: "bg-slate-700/40 text-slate-300 border-slate-700",
};
const CATEGORIES = [
  { key: "all",        label: "All" },
  { key: "sales",      label: "Sales" },
  { key: "operations", label: "Operations" },
  { key: "financial",  label: "Financial" },
];

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filter !== "all") params.category = filter;
      if (unreadOnly) params.unread_only = true;
      const r = await api.get("/notifications/inbox", { params });
      setItems(r.data || []);
    } catch { toast.error("Failed to load notifications"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter, unreadOnly]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    toast.success("All marked as read");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/notifications/${id}`);
    setItems((prev) => prev.filter((n) => n.id !== id));
  };

  const markOne = async (n) => {
    if (n.read) return;
    await api.post(`/notifications/${n.id}/read`);
    setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
  };

  const unreadCount = items.filter((n) => !n.read).length;

  return (
    <div className="space-y-5" data-testid="notifications-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Bell className="w-7 h-7 text-blue-400" /> Notifications
          </h1>
          <p className="text-slate-400 text-sm mt-1">Every important event in the system — leads, jobs, payments, commissions.</p>
        </div>
        {unreadCount > 0 && (
          <Button onClick={markAll} className="bg-blue-600 hover:bg-blue-500" data-testid="mark-all-read-btn">
            <CheckCheck className="w-4 h-4 mr-2" /> Mark all read ({unreadCount})
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              data-testid={`filter-${c.key}`}
              className={`px-3 py-1 rounded-md text-xs uppercase tracking-wider font-medium border ${filter === c.key ? "border-blue-500/40 bg-blue-500/10 text-blue-300" : "border-slate-800 text-slate-400 hover:border-slate-600"}`}
            >{c.label}</button>
          ))}
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-400">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} className="accent-blue-500" data-testid="unread-only-toggle" />
          Unread only
        </label>
      </div>

      {loading ? (
        <div className="text-center p-12"><Loader2 className="w-6 h-6 animate-spin text-blue-400 mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-12 text-center" data-testid="notifications-empty-state">
          <Bell className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-semibold">No notifications</p>
          <p className="text-slate-500 text-sm mt-1">Events will appear here as they happen.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const Icon = ICON_MAP[n.icon] || Bell;
            const cc = COLOR_MAP[n.color] || COLOR_MAP.slate;
            return (
              <div key={n.id} className={`rounded-lg border ${n.read ? "border-slate-800" : "border-blue-500/30"} bg-[#0B1120] p-4 flex items-start gap-3 group`} data-testid={`notification-row-${n.id}`}>
                <div className={`w-9 h-9 rounded-lg ${cc} flex items-center justify-center shrink-0 border`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-100">{n.title}</span>
                    <Badge className="bg-slate-800 border-slate-700 text-slate-400 text-[9px] uppercase tracking-wider">{n.category}</Badge>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-blue-400" />}
                  </div>
                  {n.message && <p className="text-xs text-slate-400 mt-1">{n.message}</p>}
                  <div className="text-[10px] text-slate-500 mt-1.5">{n.created_at?.slice(0, 16).replace("T", " ")}{n.actor ? ` · ${n.actor}` : ""}</div>
                </div>
                <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {n.link && (
                    <Link to={n.link} onClick={() => markOne(n)} className="text-xs text-blue-300 hover:text-blue-200 px-2 py-1 rounded border border-blue-500/30" data-testid={`open-${n.id}`}>Open</Link>
                  )}
                  {!n.read && (
                    <button onClick={() => markOne(n)} className="text-xs text-slate-400 hover:text-slate-200 px-2 py-1 rounded border border-slate-700" data-testid={`mark-${n.id}`}>Mark</button>
                  )}
                  <button onClick={() => remove(n.id)} className="text-slate-500 hover:text-rose-400 p-1" data-testid={`delete-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
